import { useState, useRef, useEffect } from 'react';

export const useVoiceCommands = ({ commands, onError }) => {
  const [isListening, setIsListening] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const recognition = useRef(null);
  const samanthaVoiceRef = useRef(null);

  // Function to wait for voices to load
  const waitForVoices = () => {
    return new Promise((resolve) => {
      let voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        resolve(voices);
        return;
      }

      const onVoicesChanged = () => {
        voices = window.speechSynthesis.getVoices();
        window.speechSynthesis.onvoiceschanged = null;
        resolve(voices);
      };
      window.speechSynthesis.onvoiceschanged = onVoicesChanged;
    });
  };

  // Initialize Samantha's voice
  useEffect(() => {
    const initializeVoice = async () => {
      const voices = await waitForVoices();
      samanthaVoiceRef.current = voices.find(voice => voice.name === 'Samantha');
      console.log('Available voices:', voices.map(v => v.name));
      console.log('Selected voice:', samanthaVoiceRef.current?.name);
    };
    initializeVoice();
  }, []);

  const speak = (text) => {
    window.speechSynthesis.cancel(); // Cancel any ongoing speech
    const utterance = new SpeechSynthesisUtterance(text);
    if (samanthaVoiceRef.current) {
      utterance.voice = samanthaVoiceRef.current;
    }
    window.speechSynthesis.speak(utterance);
  };

  const initializeSpeechRecognition = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      const error = 'Speech recognition is not supported in this browser. Please try Chrome, Edge, or Safari.';
      setErrorMessage(error);
      onError?.(error);
      return false;
    }

    try {
      // Request microphone permission
      const permissionResult = await navigator.permissions.query({ name: 'microphone' });
      
      if (permissionResult.state === 'denied') {
        const error = 'Microphone permission is required for voice commands. Please enable it in your browser settings.';
        setErrorMessage(error);
        onError?.(error);
        return false;
      }

      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = false;

      recognition.current.onresult = (event) => {
        const command = event.results[event.results.length - 1][0].transcript.toLowerCase().trim();
        handleVoiceCommand(command);
      };

      recognition.current.onend = () => {
        if (isListening) {
          try {
            recognition.current.start();
          } catch (error) {
            console.error('Error restarting recognition:', error);
            setIsListening(false);
            const errorMsg = 'Failed to restart voice recognition. Please try again.';
            setErrorMessage(errorMsg);
            onError?.(errorMsg);
          }
        } else {
          setIsListening(false);
        }
      };

      recognition.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        let errorMsg = '';
        switch (event.error) {
          case 'network':
            errorMsg = 'Network error occurred. Please check your connection.';
            break;
          case 'not-allowed':
            errorMsg = 'Microphone access was denied. Please enable it in your browser settings.';
            break;
          case 'no-speech':
            return; // Don't show error for no-speech in continuous mode
          default:
            errorMsg = `Error: ${event.error}`;
        }
        
        if (errorMsg) {
          setErrorMessage(errorMsg);
          onError?.(errorMsg);
        }
        setIsListening(false);
      };

      return true;
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
      const errorMsg = 'Failed to initialize speech recognition. Please try again.';
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      return false;
    }
  };

  const handleVoiceCommand = (command) => {
    console.log('Received command:', command);
    
    const cleanCommand = command.toLowerCase().trim().replace(/[.,?!]/g, '');
    console.log('Cleaned command:', cleanCommand);
    
    // Find matching command using more flexible matching
    const matchedCommand = Object.entries(commands).find(([key]) => 
      cleanCommand.includes(key.toLowerCase()) || key.toLowerCase().includes(cleanCommand)
    );

    if (matchedCommand) {
      const [key, handler] = matchedCommand;
      console.log('Matched command:', key);
      
      if (typeof handler === 'function') {
        const result = handler(cleanCommand);
        if (result && typeof result === 'string') {
          speak(result);
        }
      } else if (typeof handler === 'object' && handler.action && handler.feedback) {
        handler.action(cleanCommand);
        speak(handler.feedback);
      }
    } else {
      const availableCommands = Object.keys(commands).join(', ');
      speak(`I'm sorry, I didn't understand that command. Available commands are: ${availableCommands}`);
    }
  };

  const toggleListening = async () => {
    if (!recognition.current) {
      const initialized = await initializeSpeechRecognition();
      if (!initialized) {
        speak("Sorry, voice recognition is not available");
        return;
      }
    }

    setErrorMessage(''); // Clear any previous errors

    if (isListening) {
      try {
        recognition.current.stop();
        setIsListening(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    } else {
      try {
        await recognition.current.start();
        setIsListening(true);
        speak("Voice commands activated. Available commands: " + Object.keys(commands).join(', '));
      } catch (error) {
        console.error('Error starting recognition:', error);
        const errorMsg = 'Failed to start voice recognition. Please try again.';
        setErrorMessage(errorMsg);
        onError?.(errorMsg);
      }
    }
  };

  return {
    isListening,
    errorMessage,
    toggleListening,
    speak
  };
}; 